<script setup>
import { ref, onMounted, computed, watch, onUnmounted } from "vue";
import { useModals } from "@/composables/useModals";
import { useFieldsStore } from "@/stores/storeFieldData";
import { useRequestsStore } from "@/stores/storeRequests";
import { useSettingsStore } from "@/stores/storeSettings";
import { useLangStore } from "@/stores/storeLang";
import { useRoute } from "vue-router";
import BaseCopyTooltip from '@/components/ui/BaseCopyTooltip.vue';

const storeField = useFieldsStore();
const storeSettings = useSettingsStore();
const storeRequests = useRequestsStore();
const route = useRoute();

const pointerEventsNone = ref(false);
const password = defineModel();

const { activeModal, modalId, modalContent, sizeWidthModal, closeModal, openModal } = useModals();
const storeLang = useLangStore();

const formatText = (text) => {
	if (!text) return ;
	let formattedText = text.replace(/\r?\n/g, "<br>");

	const urlPattern = /(?<!<a href=")(https?:\/\/[^\s]+)(?!")/g;

	formattedText = formattedText.replace(urlPattern, '<a href="$1" target="_blank">$1</a>');

	return formattedText;
};

onUnmounted(() => {
	pointerEventsNone.value = false;
});
</script>
<template>
	<div style="position: absolute; bottom: 100%; overflow: hidden">
		<div id="apm-widget"></div>
	</div>
	<div :class="['modal', { 'is-open': activeModal }]" @click.self="closeModal">
		<Transition>
			<div v-if="activeModal" :class="['modal__container', sizeWidthModal]">
				<button class="modal-close" @click="closeModal">
					<svg width="24" height="24"><use href="#x"></use></svg>
				</button>
				<!-- Таблица размеров -->
				<div class="modal-content" v-if="modalId === 'sizeChart'">
					<h5 class="modal-title">{{ storeLang.t.modals.sizeChartTitle }}</h5>
					<div class="modal-size-chart">
						<div class="modal-size-chart__item --header --left">{{ storeLang.t.modals.sizeLabel }}</div>
						<div class="modal-size-chart__item --header --center">{{ storeLang.t.modals.chestLabel }}</div>
						<div class="modal-size-chart__item --header">{{ storeLang.t.modals.waistLabel }}</div>
						<div class="modal-size-chart__item --left">S-M</div>
						<div class="modal-size-chart__item --center">86</div>
						<div class="modal-size-chart__item">68</div>
						<div class="modal-size-chart__item --left">L-XL</div>
						<div class="modal-size-chart__item --center">88</div>
						<div class="modal-size-chart__item">72</div>
						<div class="modal-size-chart__item --left">XXL-XXXL</div>
						<div class="modal-size-chart__item --center">92</div>
						<div class="modal-size-chart__item">74</div>
					</div>
				</div>

				<!-- Подробнее о сертификате -->
				<div class="modal-content" v-if="modalId === 'partner_code'">
					<h5 class="modal-title" v-if="modalContent.name">{{ modalContent.name }}</h5>					
					<div class="modal-column sert-content">
						<div class="cert-copy" v-if="modalContent.partner_code">
							<div class="cert-copy__number">{{ modalContent.partner_code }}</div>
							<BaseCopyTooltip class="cert-copy__btn " :duration="2000" :title="storeLang.t.modals.copyTitle" :text="storeLang.t.modals.copiedText">
								<template #default="{ show }">
									<span @click="show(modalContent.partner_code)">
										<svg width="24" height="24"><use href="#copy_blue"></use></svg>
									</span>
								</template>
							</BaseCopyTooltip>
						</div>
						<h6 class="sert-content__date">{{ storeLang.t.certPage.promoDate }}</h6>
						<div class="sert-content__row">
							<h5 class="sert-content__list-title">{{ storeLang.t.certPage.newSubTitle }}</h5>
							<ul class="sert-content__list">
								<li class="sert-content__item" v-for="(step, i) in storeLang.t.certPage.newSubSteps" :key="i" v-html="step"></li>								
							</ul>
						</div>
						<div class="sert-content__row">
							<h5 class="sert-content__list-title">{{ storeLang.t.certPage.existSubTitle }}</h5>
							<ul class="sert-content__list">
								<li class="sert-content__item" v-for="(step, i) in storeLang.t.certPage.existSubSteps" :key="i" v-html="step"></li>
							</ul>
						</div>
					</div>
				</div>

				<!-- Подробнее о футболке -->
				<div class="modal-content" v-if="modalId === 'tshirt'">
					<h5 class="modal-title">{{ storeLang.t.modals.tshirtModalTitle }}</h5>
					<div class="modal-prize">
						<div class="modal-prize__item --border" v-if="modalContent.tshirt_size">
							<span>{{ storeLang.t.modals.sizeLabel }}</span>
							<span>{{ modalContent.tshirt_size }}</span>
						</div>
						<div class="modal-prize__item --border" v-if="modalContent.uzpost_tracking_id">
							<span>{{ storeLang.t.modals.trackLabel }}</span>
							<span>{{ modalContent.uzpost_tracking_id }}</span>
							<BaseCopyTooltip class="modal-prize__copy-btn " :duration="2000" :title="storeLang.t.modals.copyTitle" :text="storeLang.t.modals.trackCopied">
								<template #default="{ show }">
									<span @click="show(modalContent.uzpost_tracking_id)">
										<svg width="24" height="24"><use href="#copy_blue"></use></svg>
									</span>
								</template>
							</BaseCopyTooltip>
						</div>
						<!-- <div class="modal-prize__item"></div> -->
						<div class="modal-prize__item" v-if="modalContent.pickup_point?.external_code">
							<span>{{ storeLang.t.claimPage.labelIndex }}</span>
							<span>{{ modalContent.pickup_point.external_code }}</span>
						</div>
						<div class="modal-prize__item" v-if="modalContent.pickup_point">
							<span>{{ storeLang.t.modals.pickupLabel }}</span>
							<span>{{ modalContent.pickup_point.name }} <br> {{ modalContent.pickup_point.address }}</span>
						</div>
						<div class="modal-prize__item" v-if="modalContent.pickup_point?.landmark">
							<span>{{ storeLang.t.modals.pickupLabel }}</span>
							<span>{{ modalContent.pickup_point.landmark }}</span>
						</div>
					</div>
				</div>

				<!-- Подробнее о призах общие -->
				<div class="modal-content" v-if="modalId === 'prizeDetail'">
					<h5 class="modal-title">{{ modalContent.name }}</h5>
					<div class="modal-subtitle">
						{{ modalContent.description }}
					</div>
				</div>

				<!-- Общая информация -->
				<div class="modal-content" v-if="modalId === 'responseText'">
					<h5 class="modal-title" v-if="!!modalContent.title">{{ modalContent.title }}</h5>
					<p class="modal-subtitle">{{ modalContent.text }}</p>
				</div>

				<!-- Общая информация -->
				<div class="modal-content" v-if="modalId === 'stockStatus'">
					<h5 class="modal-title">{{ storeLang.t.stockStatus['endedTitle'] }}</h5>
				</div>
			</div>
		</Transition>
	</div>
</template>
<style lang="scss" scoped>
@use "@/assets/scss/base/vars";

.v-enter-active,
.v-leave-active {
	transition: 0.5s ease;
}

.v-enter-from {
	opacity: 0;
	transform: translateY(-10%);
}
.v-leave-to {
	opacity: 0;
	transform: translateY(-10%);
}

.modal {
	--transition-time: 0.3s;
	position: fixed;
	left: 0;
	top: 0;
	right: 0;
	bottom: 0;
	z-index: 10001;
	background-color: rgba(0, 0, 0, 0.7);
	// backdrop-filter: blur(10.800000190734863px);
	cursor: pointer;
	overflow-y: auto;
	overflow-x: hidden;
	text-align: center;
	opacity: 0;
	visibility: hidden;
	transition: opacity var(--transition-time), visibility var(--transition-time);
}

.modal::before {
	content: "";
	display: inline-block;
	vertical-align: middle;
	height: 100%;
}

.modal__container {
	position: relative;
	width: 393px;
	margin: 50px auto;
	display: none;
	vertical-align: middle;
	backdrop-filter: blur(14.199999809265137px);
	box-shadow: 0 4px 0 0 var(--brand-accent-1-100);
	background: var(--fill-default-1);
	border: 1px solid var(--border-accent-1-1);
	border-radius: 2.4rem;
	cursor: default;
	text-align: left;
	padding: 0;
	@media (max-width: 425px) {
		width: 95%;
	}
}

.modal.is-open {
	opacity: 1;
	visibility: visible;
	transition: opacity var(--transition-time), visibility var(--transition-time);
	.modal__container {
		display: inline-block;
	}
}

.modal__container.modal-open {
	display: inline-block;
}

.fade {
	opacity: 0;
	transition: opacity var(--transition-time);
}

.fade.animate-open {
	opacity: 1;
	transition: opacity var(--transition-time);
}

.fadeInUp {
	opacity: 0;
	transform: translateY(-100px);
	transition: opacity var(--transition-time), transform var(--transition-time);
}

.fadeInUp.animate-open {
	opacity: 1;
	transform: translateY(0);
	transition: opacity var(--transition-time), transform var(--transition-time);
}

.disable-scroll {
	position: relative;
	overflow: hidden;
	height: 100vh;
	position: fixed;
	left: 0;
	top: 0;
	width: 100%;
}

.modal-close {
	display: flex;
	align-items: center;
	justify-content: center;
	position: absolute;
	border: 1px solid var(--border-default-1);
	border-radius: 0.8rem;
	width: 3.2rem;
	height: 3.2rem;
	backdrop-filter: blur(14.199999809265137px);
	box-shadow: 0 4px 0 0 var(--brand-accent-1-100);
	background: var(--fill-accent-1-1);
	top: 1.5rem;
	right: 2rem;
	svg {
		width: 2.4rem;
    	height: 2.4rem;
		stroke-width: 1px;
		stroke:  var(--border-default-1);
		transition: transform var(--transition) ease-in;
	}
}

.modal-content {
	display: grid;
	gap: 16px;
	padding: 16px 20px;
}

.modal-column {
	display: grid;
	gap: 20px;
	max-height: 400px;
	overflow-y: auto;
}

.modal-title {
	max-width: 29rem;
	font-family: var(--third-family);
	font-weight: 900;
	font-size: 1.6rem;
	line-height: 140%;
	text-transform: uppercase;
	@media (max-width: vars.$x360) {
		font-size: 1.5rem;
		max-width: 27rem;
	}
}
.modal-subtitle {
	font-weight: 900;
	font-size: 2rem;
	line-height: 100%;
	white-space: pre-line;
	a {
		color: inherit;
		@media (hover: hover) {
			&:hover {
				text-decoration: underline;
			}
		}
	}
	&.--center {
		text-align: center;
	}
}
.modal-size-chart {
	display: grid;
	grid-template-columns: repeat(3, 1fr);
	grid-template-rows: 29px repeat(3, 26px);
	gap: 2px;
	margin: 20px -20px -16px;
}
.modal-size-chart__item {
	width: 100%;
	height: 100%;
	display: flex;
	align-items: center;
	justify-content: center;
	font-weight: 900;
	font-size: 2rem;
	line-height: 100%;
	background: var(--fill-accent-1-2);
	text-align: center;
	&.--header {
		background: var(--fill-accent-1-1);
		font-weight: 900;
		font-size: 1.5rem;
		line-height: 100%;
		background: var(--fill-accent-1-1);
	}
	&.--center {
		border-radius: 0.8rem;
	}
	&.--left {
		border-radius: 0 0.8rem 0.8rem 0;
	}
	&.--right {
		border-radius: 0.8rem 0 0 0.8rem;
	}
}

.cert-copy {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 1rem;
	height: 4rem;
	border: 1px solid var(--border-accent-1-1);
	border-radius: 1.6rem;
	padding: 2px 16px;
}
.cert-copy__number {
	font-weight: 900;
	font-size: 2rem;
	line-height: 160%;
}
.input__field {
}
.cert-copy__btn {

	span {

		width: 2.4rem;
		height: 2.4rem;
	}
	svg {
		stroke: transparent;
		fill: #1C6FFF;
	}
}

.modal-prize {
	display: grid;
	gap: 1rem;
}
.modal-prize__item {
	position: relative;
	display: grid;
	&.--border {
		padding-bottom: 1rem;
		border-bottom: 1px solid var(--border-accent-1-1);
	}

	span {
		font-weight: 900;
		font-size: 2rem;
		line-height: 120%;
		&:first-child {
			color: var(--text-accent-1-100);
		}
	}
}
.modal-prize__copy-btn {
	position: absolute;
	top: 50%;
	transform: translateY(-50%);
	right: 5px;

	width: 2.4rem;
	height: 2.4rem;

	svg {
		stroke: transparent;
		fill: #1C6FFF;
	}
}
.sert-content__item {
	:deep(a) {
		color: inherit;
		text-decoration: underline;
		@media (hover: hover) {
			&:hover {
			}
		}
	}
}
</style>
